'use client'

import { motion } from 'framer-motion'
import { Store, Package, ArrowRight, TrendingUp, Users, Truck, Sparkles, Megaphone, Mail, Globe, Zap, ShoppingBag } from 'lucide-react'
import { Container } from '@/components/ui/container'
import { Section } from '@/components/ui/section'
import { Button } from '@/components/ui/button'
import { siteConfig, productStats } from '@/lib/constants'

const audiences = [
  { icon: Store, label: 'Suppliers' },
  { icon: Package, label: 'Retailers' },
  { icon: Truck, label: 'Dropshippers' },
  { icon: TrendingUp, label: 'Wholesalers' },
  { icon: ShoppingBag, label: 'Marketplace Sellers' },
  { icon: Users, label: 'Brand Owners' },
]

export function SupplierRetailerSection() {
  return (
    <Section background="white">
      <Container>
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5 }}
          className="text-center mb-12"
        >
          <p className="text-micro-uppercase text-brand-green mb-3">WHO THIS IS FOR</p>
          <h2 className="text-display-1 text-ink mb-4">
            Built for Suppliers and Retailers Alike
          </h2>
          <p className="text-body-lg text-muted max-w-2xl mx-auto">
            One platform that serves both sides of a partnership — quality suppliers get reach, and retailers get ready-to-sell products.
          </p>
        </motion.div>

        <div className="grid md:grid-cols-2 gap-6 max-w-5xl mx-auto">
          {/* Supplier card */}
          <motion.div
            initial={{ opacity: 0, x: -20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-2xl bg-gradient-to-br from-primary-600 to-primary-700 border border-primary-500/30 p-8"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Store className="w-32 h-32 text-white" />
            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-white/15 text-white text-xs font-semibold mb-6">
                <Sparkles className="w-3.5 h-3.5" />
                Supplier side
              </div>

              <h3 className="text-2xl font-bold text-white mb-3">
                Have products? Build quality retail partnerships.
              </h3>
              <p className="text-primary-100 mb-6">
                Connect with like-minded brands for long-term, local partnerships that extend your reach.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'Partner with complementary local brands',
                  'Extend AI and SEO visibility across partner stores',
                  'Run joint marketing & newsletter campaigns',
                  'Automated order routing — no manual work',
                  'Zero commissions on partner sales',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-primary-100">
                    <Megaphone className="w-5 h-5 text-white shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>

              <Button
                href={siteConfig.links.shopifyApp}
                external
                className="justify-center bg-panel text-brand-green hover:bg-brand-green-soft"
              >
                Become a Supplier
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>

          {/* Retailer card */}
          <motion.div
            initial={{ opacity: 0, x: 20 }}
            whileInView={{ opacity: 1, x: 0 }}
            viewport={{ once: true }}
            transition={{ duration: 0.5 }}
            className="relative overflow-hidden rounded-2xl bg-panel border border-line p-8"
          >
            <div className="absolute top-0 right-0 p-4 opacity-10">
              <Package className="w-32 h-32 text-brand-green" />
            </div>

            <div className="relative z-10">
              <div className="inline-flex items-center gap-2 px-3 py-1 rounded-full bg-brand-green-soft text-brand-green text-xs font-semibold mb-6">
                <Zap className="w-3.5 h-3.5" />
                Retailer side
              </div>

              <h3 className="text-2xl font-bold text-ink mb-3">
                Need products? Expand your catalog in one click.
              </h3>
              <p className="text-muted mb-6">
                Source from verified suppliers, set your margins, and let them handle fulfillment.
              </p>

              <ul className="space-y-3 mb-8">
                {[
                  'One-click import from many local brands',
                  'Verified suppliers only',
                  'Auto inventory sync & order fulfillment',
                  'Wholesale pricing with healthy margins',
                  'No inventory risk, no commissions',
                ].map((item) => (
                  <li key={item} className="flex items-start gap-3 text-muted">
                    <Truck className="w-5 h-5 text-brand-green shrink-0 mt-0.5" />
                    {item}
                  </li>
                ))}
              </ul>

              <Button href={siteConfig.links.shopifyApp} external className="justify-center">
                Start Dropshipping
                <ArrowRight className="w-4 h-4" />
              </Button>
            </div>
          </motion.div>
        </div>

        {/* Audience chips */}
        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.2 }}
          className="flex flex-wrap justify-center gap-3 mt-10"
        >
          {audiences.map((item, index) => {
            const Icon = item.icon
            return (
              <motion.div
                key={item.label}
                initial={{ opacity: 0, scale: 0.9 }}
                whileInView={{ opacity: 1, scale: 1 }}
                viewport={{ once: true }}
                transition={{ delay: index * 0.05 }}
                className="flex items-center gap-2 px-4 py-2 rounded-full bg-raised text-muted text-sm"
              >
                <Icon className="w-4 h-4 text-brand-green" />
                {item.label}
              </motion.div>
            )
          })}
        </motion.div>

        <motion.div
          initial={{ opacity: 0, y: 20 }}
          whileInView={{ opacity: 1, y: 0 }}
          viewport={{ once: true }}
          transition={{ duration: 0.5, delay: 0.3 }}
          className="mt-10 flex flex-wrap justify-center gap-8 text-center"
        >
          <div className="flex items-center gap-2">
            <Users className="w-5 h-5 text-brand-green" />
            <span className="text-muted">{productStats.brandPartnerships.partnerships} active partnerships</span>
          </div>
          <div className="flex items-center gap-2">
            <Globe className="w-5 h-5 text-brand-green" />
            <span className="text-muted">{productStats.brandPartnerships.countries}+ countries</span>
          </div>
          <div className="flex items-center gap-2">
            <Mail className="w-5 h-5 text-brand-green" />
            <span className="text-muted">Joint campaigns & co-marketing</span>
          </div>
        </motion.div>
      </Container>
    </Section>
  )
}
